import { useEffect, useState } from "react";

import { useExtendLoan, useReturnLoan } from "@/api/queries";
import type { AssetCondition, Loan } from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Field, Input, Select } from "@/components/Input";
import { useToast } from "@/components/toast-context";
import { formatAssetCondition, formatDate, formatOverdue } from "@/lib/format";

const CONDITIONS: AssetCondition[] = [
  "NEW",
  "GOOD",
  "WORN",
  "NEEDS_REPAIR",
  "DAMAGED",
  "UNKNOWN",
];

/**
 * Take a tool back, or push its return date out (Frontend Spec §17).
 *
 * The condition on the way back is the point of the screen. It is written onto
 * the asset as well as onto the loan: a drill that came back damaged is damaged
 * from now on, not only inside the record of one loan.
 */
export function ReturnDialog({
  loan,
  onOpenChange,
}: {
  loan: Loan | null;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const returnLoan = useReturnLoan();
  const extendLoan = useExtendLoan();

  const [condition, setCondition] = useState<AssetCondition | "">("");
  const [notes, setNotes] = useState("");
  const [newDueOn, setNewDueOn] = useState("");

  useEffect(() => {
    if (loan) {
      setCondition(loan.condition_out);
      setNotes("");
      setNewDueOn("");
    }
  }, [loan]);

  async function submitReturn() {
    if (!loan) return;
    try {
      const done = await returnLoan.mutateAsync({
        loanId: loan.id,
        condition_in: condition || null,
        notes: notes || null,
      });
      toast.success("Outil rentré", `${done.asset_code} — ${formatAssetCondition(done.condition_in ?? "UNKNOWN")}`);
      onOpenChange(false);
    } catch (error) {
      toast.error("Retour refusé", String(error));
    }
  }

  async function submitExtension() {
    if (!loan || !newDueOn) return;
    try {
      const done = await extendLoan.mutateAsync({ loanId: loan.id, due_on: newDueOn });
      toast.success("Prêt prolongé", `Retour prévu le ${formatDate(done.due_on)}`);
      onOpenChange(false);
    } catch (error) {
      toast.error("Prolongation refusée", String(error));
    }
  }

  const late = loan ? formatOverdue(loan.days_overdue) : null;

  return (
    <Dialog
      open={Boolean(loan)}
      onOpenChange={onOpenChange}
      title="Retour d'outil"
      description={
        loan
          ? `${loan.asset_code} — ${loan.asset_name ?? loan.product_name ?? ""} · ${loan.person_name}`
          : undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submitReturn()}
            disabled={returnLoan.isPending}
          >
            {returnLoan.isPending ? "Enregistrement…" : "Confirmer le retour"}
          </Button>
        </>
      }
    >
      {loan && (
        <div className="flex flex-col gap-3">
          <dl className="grid grid-cols-2 gap-2 rounded-md border border-border bg-surface-sunken p-3 text-[12px]">
            <div>
              <dt className="text-ink-subtle">Sorti le</dt>
              <dd className="text-ink">{formatDate(loan.checked_out_at)}</dd>
            </div>
            <div>
              <dt className="text-ink-subtle">Retour prévu</dt>
              <dd className={late ? "text-status-out" : "text-ink"}>
                {loan.due_on ? formatDate(loan.due_on) : "Sans échéance"}
                {late ? ` · ${late}` : ""}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="text-ink-subtle">État à la sortie</dt>
              <dd className="text-ink">{formatAssetCondition(loan.condition_out)}</dd>
            </div>
          </dl>

          <Field
            label="État au retour"
            hint="Il devient l'état de l'outil. C'est la paire sortie/retour qui tranche une discussion."
          >
            <Select
              value={condition}
              onChange={(event) => setCondition(event.target.value as AssetCondition)}
              aria-label="État au retour"
            >
              {CONDITIONS.map((value) => (
                <option key={value} value={value}>
                  {formatAssetCondition(value)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Note" hint="Facultatif.">
            <Input
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Accessoire manquant, bruit anormal…"
            />
          </Field>

          <div className="border-t border-border pt-3">
            <Field
              label="Ou prolonger le prêt"
              hint="L'outil n'est pas rentré, seule l'échéance change. Une date ne peut qu'avancer."
            >
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={newDueOn}
                  onChange={(event) => setNewDueOn(event.target.value)}
                  aria-label="Nouvelle date de retour"
                />
                <Button
                  variant="secondary"
                  onClick={() => void submitExtension()}
                  disabled={!newDueOn || extendLoan.isPending}
                >
                  Prolonger
                </Button>
              </div>
            </Field>
          </div>
        </div>
      )}
    </Dialog>
  );
}
